import { useState, useEffect } from 'react';
import { Sort } from '../../types';
import RegistrantsTable from './registrants-table';
import Container from '@ui/container/container';
import { useAppSelector } from '@/store/hooks';
import { selectRegistrantsLoading, selectRegistrants, selectProjectCourse } from '@/store/slices/registrants/selectors';
import Text from '@ui/text/text/text';
import OptionalComponent from '@ui/optional-component/optional-component';
import AudiencesFilters from '../audiences-filters';
import { useSearchParams, NavLink, useNavigate } from 'react-router-dom';
import { selectLoadingPasscodeLists, selectMemberPasscodeListsLoading } from '@/store/slices/passcode-lists/selectors';
import { useLoadRegistrants } from '../../hooks/use-load-registrants';
import {
  selectProjectChannel,
  selectProjectStartTimestamp,
  selectProjectCreatedDate,
  selectWorkingProjectMagicLinkRegistrationsPasscodeListId,
  selectIsCourseEnabled,
  selectWorkingProjectUuid,
} from '@/store/slices/project/selectors';
import { AUDIENCES_VIEW, REGISTRANT_FILTER_TYPE, REGISTRANT_LIST_PAGE_SIZE } from '@/types/registration.types';
import classNames from 'classnames';
import styles from '../listsv2/passcode-lists.module.css';
import Icon from '@ui/icons/icon';
import { ICONS } from '@ui/icons/types';
import { getFilterTitleFromStatus } from '../../default-passcode-lists';
import ManageRegistrantsModal from './modals/add-registrants-modal';
import { useSaveRegistrantList } from '../../hooks/use-save-registrant-list';
import UploadRegistrantsModal from './modals/upload-registrants-modal';
import { useProjectCourse } from '../../hooks/use-project-course';
import useLanguageParams from '@/utils/use-language-params';
import TableListDenseSkeletonLoader from '@ui/skeleton-loader/custom/table-list-dense-skeleton-loader';

export default function Registrants() {
  const projectUuid = useAppSelector(selectWorkingProjectUuid);
  const language = useLanguageParams();
  const registrantsLoading = useAppSelector(selectRegistrantsLoading);
  const registrants = useAppSelector(selectRegistrants);
  const memberPasscodeListsLoading = useAppSelector(selectMemberPasscodeListsLoading);
  const loadingPasscodeLists = useAppSelector(selectLoadingPasscodeLists);
  const channel = useAppSelector(selectProjectChannel);
  const startTimestamp = useAppSelector(selectProjectStartTimestamp);
  const createdDate = useAppSelector(selectProjectCreatedDate);
  const magicLinkRegistrantsPasscodeListId = useAppSelector(selectWorkingProjectMagicLinkRegistrationsPasscodeListId);
  const coursesEnabled = useAppSelector(selectIsCourseEnabled);
  const course = useAppSelector(selectProjectCourse);

  useProjectCourse(channel || 0, projectUuid || '', coursesEnabled || false);

  const { saveMagicLinksRegistrationList, saving } = useSaveRegistrantList();

  const isLoading = registrantsLoading || memberPasscodeListsLoading || loadingPasscodeLists;

  const navigate = useNavigate();
  const [sortRegistrant, setSortRegistrant] = useState<Sort | undefined>();
  const [showAddRegModal, setShowAddRegModal] = useState(false);
  const [showUploadRegModal, setShowUploadRegModal] = useState(false);

  const [searchParams] = useSearchParams();

  const hasFiltersApplied = !!(searchParams.get('search') || searchParams.get('lists') || searchParams.get('status'));

  const pageSize = REGISTRANT_LIST_PAGE_SIZE;
  const defaultStartTimestamp =
    startTimestamp || (createdDate ? new Date(createdDate).getTime() : Date.now() - 30 * 24 * 60 * 60 * 1000);

  const { loadRegistrants } = useLoadRegistrants(
    projectUuid || '',
    pageSize,
    magicLinkRegistrantsPasscodeListId || 0,
    true,
    !!course,
    channel,
    defaultStartTimestamp,
    Date.now()
  );

  const status = searchParams.get('status') || '';

  useEffect(() => {
    if (!status) {
      navigate(`/project/edit/${projectUuid}/${language}/audiences/registrants?status=all`);
    }
  }, [status, projectUuid, language, navigate]);

  useEffect(() => {
    if (status) {
      loadRegistrants();
    }
  }, [status, loadRegistrants]);

  return (
    <>
      <Container className="position-relative flex-column gap-6" minHeight="calc(100vh - 500px)">
        <Container className="flex ai-center">
          <NavLink
            className={classNames(styles['passcode-list-back-button'], 'flex ai-center padding-r-8')}
            to={`/project/edit/${projectUuid}/${language}/audiences`}
          >
            <Icon name={ICONS.ARROW_LEFT} size={20} />
          </NavLink>
          <Text tag="h2">{getFilterTitleFromStatus(searchParams.get('status') || '')}</Text>
        </Container>
        <Container
          opacity={isLoading ? 1 : 0}
          className="flex ai-flex-start position-absolute z-10 top-0 left-0 right-0 bottom-0"
          style={{
            visibility: isLoading ? 'visible' : 'hidden',
            transition: 'opacity 300ms ease-in-out',
            position: 'absolute',
            top: 48,
          }}
        >
          <TableListDenseSkeletonLoader />
        </Container>
        <Container
          opacity={isLoading ? 0 : 1}
          style={{
            transition: 'opacity 300ms ease-in-out',
            pointerEvents: isLoading ? 'none' : 'auto',
            visibility: isLoading ? 'hidden' : 'visible',
          }}
        >
          <OptionalComponent display={registrants.length === 0 && hasFiltersApplied}>
            <Container className="flex-column jc-center ai-center gap-12 padding-t-100">
              <Text className="H2">No registrants to show</Text>
            </Container>
          </OptionalComponent>
          <OptionalComponent display={!isLoading && registrants.length > 0}>
            <RegistrantsTable sort={sortRegistrant} setSort={setSortRegistrant} status={status} />
          </OptionalComponent>
        </Container>
        <Container className="position-absolute right-0" style={{ top: '0' }}>
          <AudiencesFilters
            expandedSearchWidth="280px"
            onAddClick={() => setShowAddRegModal(true)}
            onUploadClick={() => setShowUploadRegModal(true)}
            activeView={AUDIENCES_VIEW.REGISTRANTS}
            status={status as REGISTRANT_FILTER_TYPE | ''}
          />
        </Container>
      </Container>
      <ManageRegistrantsModal
        open={showAddRegModal}
        setOpen={setShowAddRegModal}
        onSave={saveMagicLinksRegistrationList}
        saving={saving}
        onSaveComplete={() => {
          setShowAddRegModal(false);
          loadRegistrants();
        }}
      />
      <UploadRegistrantsModal
        open={showUploadRegModal}
        setOpen={setShowUploadRegModal}
        onSave={saveMagicLinksRegistrationList}
        saving={saving}
        onSaveComplete={() => {
          setShowUploadRegModal(false);
          loadRegistrants();
        }}
      />
    </>
  );
}
